import { useCallback, useMemo } from 'react';
import { useCoordinateExtraction } from './useCoordinateExtraction';
import { useMapZoom } from './useMapZoom';
import { AutoCompleteShipInfo } from '@ais-ui/websockets';
import { useShipSelection } from '../../../Map/contexts';
import {
  normalizeRowForTarget,
  hasDualMMSI,
  getMMSI_A,
  getMMSI_B,
  getName_A,
  getName_B,
} from './rowUtils';

export interface RowAction {
  label: string;
  onClick: (row: any) => void;
  icon?: React.ReactNode;
}

export interface UseTableActionsProps {
  shipData: any[] | undefined;
}

/**
 * Custom hook for generating table row actions based on available data
 * Dynamically creates actions based on what data is present in the rows
 */
export const useTableActions = ({ shipData }: UseTableActionsProps) => {
  const { extractCoordinates, hasPositionData } = useCoordinateExtraction();
  const { zoomToCoordinates } = useMapZoom();

  const { setZoomToShip } = useShipSelection();

  const goToTargetWithMMSI = useCallback(
    (mmsi: string, name?: string) => {
      const autoCompleteShip: AutoCompleteShipInfo = {
        name: name && name !== mmsi ? name : '',
        mmsi: mmsi,
        type: 'TT_SHIP',
        callSign: '',
        imo: '',
      };
      setZoomToShip(autoCompleteShip);
    },
    [setZoomToShip],
  );

  const onGoToTarget = useCallback(
    (row: any) => {
      const normalizedRow = normalizeRowForTarget(row);

      const mmsi = normalizedRow?.mmsi || normalizedRow?.['to mmsi'];

      // The backend sets the mmsi as the name in some cases, and if we try to search by mmsi name, it'll not be found
      const name = normalizedRow?.name !== mmsi ? normalizedRow?.name : '';
      const autoCompleteShip: AutoCompleteShipInfo = {
        name: name,
        mmsi: mmsi ? mmsi : '',
        imo: normalizedRow?.imo,
        callSign: normalizedRow?.callSign,
        type: 'TT_SHIP',
      };
      setZoomToShip(autoCompleteShip);
    },
    [setZoomToShip],
  );

  // Actions only depend on the column structure of the data, not its values.
  // Keying on a header signature keeps the actions (and thus table columns)
  // referentially stable across websocket data ticks, so an open row-action
  // dropdown stays open long enough to be clicked.
  const headerSignature =
    shipData && shipData.length > 0 ? Object.keys(shipData[0]).join('|') : '';

  const actions = useMemo((): RowAction[] => {
    if (!headerSignature) {
      return [];
    }

    const keys = headerSignature.split('|');
    const sampleRow = Object.fromEntries(keys.map((key) => [key, '']));
    const rowActions: RowAction[] = [];

    // Check if this is a ship-to-ship report with dual MMSI columns
    const isDualMMSI = hasDualMMSI(sampleRow);

    if (isDualMMSI) {
      // Add separate actions for vessel A and vessel B
      rowActions.push({
        label: 'View target A',
        onClick: (row) => {
          const mmsi = getMMSI_A(row);
          const name = getName_A(row);
          if (mmsi) {
            goToTargetWithMMSI(String(mmsi), name ? String(name) : undefined);
          }
        },
      });
      rowActions.push({
        label: 'View target B',
        onClick: (row) => {
          const mmsi = getMMSI_B(row);
          const name = getName_B(row);
          if (mmsi) {
            goToTargetWithMMSI(String(mmsi), name ? String(name) : undefined);
          }
        },
      });
    } else {
      // Check if MMSI is present for single "View Target" action
      const hasMMSI = keys.some((key) => key.toLowerCase().includes('mmsi'));

      if (hasMMSI) {
        rowActions.push({
          label: 'View target',
          onClick: (row) => {
            onGoToTarget(row);
          },
        });
      }
    }

    // Check if position data is present for "Zoom to Event" action
    const hasPosition = hasPositionData(sampleRow);

    if (hasPosition) {
      rowActions.push({
        label: 'Zoom to event',
        onClick: (row) => {
          const coordinates = extractCoordinates(row);

          if (coordinates) {
            zoomToCoordinates(coordinates);
          } else {
            console.warn('Could not extract coordinates from row:', row);
          }
        },
      });
    }

    return rowActions;
  }, [
    headerSignature,
    extractCoordinates,
    hasPositionData,
    zoomToCoordinates,
    onGoToTarget,
    goToTargetWithMMSI,
  ]);

  /**
   * Get actions for a specific row (useful if actions vary per row)
   */
  const getActionsForRow = useMemo(() => {
    return (row: any): RowAction[] => {
      // For now, all rows have the same actions
      // But this could be extended to filter actions based on row data
      return actions.filter((action) => {
        // Example: Only show "Zoom to event" if this specific row has coordinates
        if (action.label === 'Zoom to event') {
          return hasPositionData(row);
        }
        return true;
      });
    };
  }, [actions, hasPositionData]);

  /**
   * Check if any actions are available
   */
  const hasActions = actions.length > 0;

  return {
    actions,
    getActionsForRow,
    hasActions,
  };
};
